// The words of the Publishing pages (keepr-api KPR-1331): a publisher's own
// packages, their status and what a build left out, and where one opens. One
// home, so the list, the package page and the dialogs say a status the same
// way.

import { normalizeId } from '../../utils/ids';

/** Where a publisher's package opens: its page in Publishing. */
export const publishingPackagePath = id => `/publishing/packages/${encodeURIComponent(normalizeId(id) || id)}`;

/** The tile a package wears: its cards' tiles for a bundle, its own otherwise (ContextBar and RecordTitle props). */
export function packageIdentity(pkg) {
	const preview = Array.isArray(pkg?.preview) ? pkg.preview : [];
	return pkg?.kind === 'bundle' && preview.length > 1
		? { records: preview }
		: { record: { icon: pkg?.icon, color: pkg?.color, name: pkg?.name } };
}

export const STATUS_LABELS = Object.freeze({ draft: 'Draft', published: 'Published', retired: 'Retired' });

/** What the status means for the publisher, in one line. */
export function statusNote(pkg) {
	switch (pkg?.status) {
	case 'draft': return 'A draft: nobody else sees it. Publish sends it for review; Discover lists it once a reviewer approves it.';
	case 'published': return 'Listed on Discover. A change to it is sent for review, and Discover keeps showing this version until a reviewer approves the new one.';
	case 'retired': return 'Retired: not on Discover. Collections that already added it keep their copies. Publish sends it for review to list it again.';
	default: return '';
	}
}

/** "Card bundle · Version 2 · acme/orders" — the kind, the version and the key, for a row or a header. */
export function packageMetaLine(pkg, kindLabels = {}) {
	if (!pkg) return '';
	return [kindLabels[pkg.kind] || pkg.kind, `Version ${pkg.version || 1}`, pkg.key].filter(Boolean).join(' · ');
}

/**
 * What a build left out, one line each (`report.skipped`: `{ kind, name,
 * reason }`), and how many forms travel. Names only — they are the
 * publisher's own collection's.
 */
export function buildReportLines(report) {
	const r = report && typeof report === 'object' ? report : {};
	const lines = (Array.isArray(r.skipped) ? r.skipped : [])
		.map(entry => (entry && entry.name ? `${entry.name}${entry.reason ? `: ${entry.reason}` : ''}` : null))
		.filter(Boolean);
	return lines;
}

/** The success line of a build: "Built “Orders” as a draft." / "…, leaving 2 things out." */
export function builtWords(name, report, { verb = 'Built', as = 'as a draft' } = {}) {
	const left = buildReportLines(report).length;
	const what = `${verb} “${name}” ${as}`.trim();
	if (!left) return `${what}.`;
	return `${what}, leaving ${left === 1 ? 'one thing' : `${left} things`} out.`;
}

/** The key a new package would get: the publisher's prefix, then the name. */
export function keyPrefixOf(publisher) {
	return publisher && publisher.key ? `${publisher.key}/` : '';
}

// ---- versions (keepr-api KPR-1327): what each version of a package is, and where it stands

/** What a version changes, after its number: "Version 2 · New content". */
export const VERSION_CHANGE_WORDS = Object.freeze({ publish: 'To be listed', rebuild: 'New content', listing: 'New listing' });

/** A version's state as its tag says it; an approved one Discover lists now says so. */
export function versionStateLabel(row) {
	switch (row?.state) {
	case 'held': return 'Waiting for review';
	case 'approved': return row.listed ? 'Listed on Discover' : 'Approved';
	case 'refused': return 'Not approved';
	case 'withdrawn': return 'Withdrawn';
	case 'superseded': return 'Replaced';
	default: return row?.state || '';
	}
}

/** Why a version left the queue without a decision, in one line, or null. */
export function versionWhyWords(row) {
	if (row?.state === 'withdrawn') {
		if (row.why === 'retired') return 'Withdrawn when the package was retired.';
		if (row.why === 'sender_left_publisher') return 'Withdrawn: the account that sent it no longer publishes for you.';
		return 'Withdrawn by the account that sent it.';
	}
	if (row?.state === 'superseded') {
		return row.why === 'newer' ? 'Replaced by a newer version before a reviewer read it.' : 'Replaced: the package changed before a reviewer read it.';
	}
	return null;
}

/** "Version 2 · New content". */
export function versionTitle(row) {
	return [`Version ${row?.version || 1}`, VERSION_CHANGE_WORDS[row?.change]].filter(Boolean).join(' · ');
}
